'use client';

/**
 * Mirrors the listings search filters into the user's listing-alert
 * criteria, and turns alerts on the first time all three basics land.
 *
 * Product rules:
 *
 *  1. While alerts are OFF and auto-enable has never fired, every
 *     change to location / max price / bedrooms is written straight to
 *     the Explorer row. Partial progress therefore accumulates
 *     server-side — someone who sets a neighborhood on their phone and
 *     adds beds + rent on their laptop a week later still gets alerts
 *     enabled, which localStorage alone can't deliver.
 *  2. The moment all three are populated, alerts flip on, the one-shot
 *     marker is stamped, and `instant_alerts_on_auto` fires.
 *  3. After that the filters stop writing entirely — permanently.
 *     Alert criteria are then owned by the /saved card and the renter
 *     profile. Browsing must never rewrite them, and clearing a filter
 *     must never switch alerts off.
 *
 * Why a global headless provider: the three fields can be completed
 * from the home hero search, the /listings filter bar, the mobile
 * filter sheet, or a shared pre-filtered URL. Mounting once in the
 * root layout covers every path with one implementation.
 *
 * `listing_alert` and `listing_alert_auto_enabled_at` are both
 * client-owned — the server persists exactly what it's sent and runs
 * no auto-enable logic of its own (see `app/views/explorer.py`).
 */

import { useCallback, useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import { updateListingAlert, type UpdateListingAlertRequest } from '@repo/api';
import { loadListingAlert, setListingAlertCache } from '@/app/saved/hooks/listingAlertStore';
import { useStore } from '@/providers/StoreProvider';
import { useAnalytics } from '@/hooks/useAnalytics';
import { hasAllListingAlertBasics, resolveListingAlertAutoSource } from '@/utils/listingAlerts';

/**
 * Filter edits arrive in bursts — selecting a neighborhood group
 * writes one store update per child id, and the price input fires on
 * each keystroke. Collapse a burst into a single write.
 */
const SYNC_DEBOUNCE_MS = 800;

const sameNumberSet = (a: number[] | null | undefined, b: number[] | null | undefined) => {
  const left = [...(a ?? [])].sort((x, y) => x - y);
  const right = [...(b ?? [])].sort((x, y) => x - y);
  return left.length === right.length && left.every((v, i) => v === right[i]);
};

const sameStringSet = (a: string[] | null | undefined, b: string[] | null | undefined) => {
  const left = [...(a ?? [])].sort();
  const right = [...(b ?? [])].sort();
  return left.length === right.length && left.every((v, i) => v === right[i]);
};

export function ListingAlertAutoEnable() {
  const sessionHydrated = useStore(s => s.webAuth.sessionHydrated);
  const isLoggedIn = useStore(s => s.webAuth.isLoggedIn);
  // Also gate on the token, not just the flag. `isLoggedIn` can flip
  // true a beat before the token lands, and firing the read in that
  // window returns 403 — same reason `UserDataHydration` waits for
  // both. Including it as a dependency also means the login
  // transition re-runs this effect, which is what picks up filters a
  // user set while logged OUT (they persist to localStorage) and
  // syncs them the moment the session is usable.
  const authToken = useStore(s => s.webAuth.customToken);
  const neighborhoodIds = useStore(s => s.listingsFilters.neighborhoodIds);
  const maxRent = useStore(s => s.listingsFilters.price.max);
  const bedrooms = useStore(s => s.listingsFilters.bedrooms);
  const analytics = useAnalytics();
  const pathname = usePathname();

  // Terminal state: alerts are on, or the one-shot marker is stamped.
  // Either way the filters have no further business writing to the
  // Explorer row, so we stop for good (resets on logout only).
  const settledRef = useRef(false);
  const inFlightRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const syncFilters = useCallback(async () => {
    if (settledRef.current || inFlightRef.current) return;
    inFlightRef.current = true;
    try {
      // Shared loader — reuses the row any mounted `useListingAlert`
      // already fetched, and coalesces with an in-flight request
      // rather than opening a second one. Always read through it (not
      // a local ref) so a write from the /saved card or the wizard is
      // reflected here immediately.
      const server = await loadListingAlert();

      // Already on, or automation already had its turn. Alerts are
      // user-managed from here; searching must not touch them.
      if (server.listingAlert || server.listingAlertAutoEnabledAt) {
        settledRef.current = true;
        return;
      }

      // Only send fields that actually differ, so a filter change that
      // doesn't touch the basics costs nothing.
      const patch: UpdateListingAlertRequest = {};
      if (
        neighborhoodIds.length > 0 &&
        !sameStringSet(neighborhoodIds, server.listingAlertNeighborhoodIds)
      ) {
        patch.listingAlertNeighborhoodIds = neighborhoodIds;
      }
      if (typeof maxRent === 'number' && maxRent !== server.listingAlertMaxRent) {
        patch.listingAlertMaxRent = maxRent;
      }
      if (bedrooms.length > 0 && !sameNumberSet(bedrooms, server.listingAlertNumBedrooms)) {
        patch.listingAlertNumBedrooms = [...bedrooms].sort((a, b) => a - b);
      }
      if (Object.keys(patch).length === 0) return;

      // Does this write complete the set? Merge the patch over the
      // server row rather than reading the filter store directly — a
      // basic may already be stored from an earlier visit or device
      // even though it's absent from the current filter state.
      const merged = { ...server, ...patch };
      const completesBasics = hasAllListingAlertBasics({
        neighborhoodIds: merged.listingAlertNeighborhoodIds,
        maxRent: merged.listingAlertMaxRent,
        bedrooms: merged.listingAlertNumBedrooms,
      });
      if (completesBasics) {
        // Flag + marker ride along in the same request so we can't end
        // up enabled-but-unstamped if the tab closes mid-sequence.
        patch.listingAlert = true;
        patch.listingAlertAutoEnabledAt = new Date().toISOString();
      }

      const updated = await updateListingAlert(patch);
      // Publish so mounted `useListingAlert` consumers re-render with
      // the row we just wrote instead of holding the pre-write one.
      setListingAlertCache(updated);

      if (completesBasics && updated.listingAlert) {
        analytics.track('instant_alerts_on_auto', {
          source: resolveListingAlertAutoSource(pathname),
        });
        settledRef.current = true;
      }
    } catch {
      // Silent by design — background automation the user never asked
      // for must not surface an error or block anything. `settledRef`
      // stays false, so the next filter change retries.
    } finally {
      inFlightRef.current = false;
    }
  }, [analytics, bedrooms, maxRent, neighborhoodIds, pathname]);

  useEffect(() => {
    if (!sessionHydrated || !isLoggedIn || !authToken) return;
    if (settledRef.current) return;
    // Nothing set yet — don't spend a read. Note we deliberately do
    // NOT treat "filters went empty" as a signal to clear the stored
    // criteria: clearing a search must never wipe alert setup.
    const hasAnyBasic =
      neighborhoodIds.length > 0 || typeof maxRent === 'number' || bedrooms.length > 0;
    if (!hasAnyBasic) return;

    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => void syncFilters(), SYNC_DEBOUNCE_MS);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [bedrooms, maxRent, neighborhoodIds, isLoggedIn, authToken, sessionHydrated, syncFilters]);

  // Reset on logout so the next session re-evaluates from scratch
  // instead of inheriting the previous user's state. The shared row
  // itself is cleared by `useListingAlert`'s own logout effect.
  useEffect(() => {
    if (!isLoggedIn) {
      settledRef.current = false;
      inFlightRef.current = false;
    }
  }, [isLoggedIn]);

  return null;
}
